/* eslint-disable no-else-return */
import { Close, Delete, Edit, FiClock, PhRCheckCircle, PhRxCircle, SwapOutlined, Website } from "@payping/icons";
import { deleteClientAppOrToken, getClientTokenList } from "@payping/redux/actions";
import { dateFormats, jalaliNow, toJalali, toUtc } from "@payping/utils/dateUtils";
import {
  Button,
  CardList,
  Drawer,
  Flex,
  Grid,
  Modal,
  Space,
  TableAction,
  Tag,
  Typography,
  useMessagedAsync,
} from "@payping/web-components";
import { useState } from "react";

import { DisplayModeEnum, ListCardProps } from "@payping/web-components/components/List/types";
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from "jalali-moment";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { ConfirmModal, ConfirmModalProps } from "@/components/DashIpg/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { TokenType } from "./TokenTypes";
import { TokenFormModal } from "./TokenFormModal";
import TokenRenewModal from "./TokenRenewModal";
import { useAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { dashboardPreferencesAtom } from "@/core/state";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

const TokenList = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { lg } = Grid.useBreakpoint();
  const [language] = useAtom(languageAtom);

  const tokenList = useAppSelector((state) => state?.client?.token?.list);

  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [renewModalOpen, setRenewModalOpen] = useState<boolean>(false);
  const initialTokenValues: TokenType = {
    isTest: false,
    scopes: ["pay:read", "pay:write"],
  };
  const [formModalData, setFormModalData] = useState<TokenType>(initialTokenValues);
  const [confirmModalData, setConfirmModalData] = useState<ConfirmModalProps>();
  const [renewModalClientId, setRenewModalClientId] = useState<string>("");
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const handleDelete = (clientId: string) => {
    if (tokenList) {
      modal.confirm({
        cancelText: t("cancel"),
        content: t("token.remove.confirm"),
        maskClosable: true,
        okText: t("ok"),
        onOk: async () => {
          dispatch(deleteClientAppOrToken(clientId as string));
        },
      });
    }
  };

  function handleRenew(clientId: string) {
    setRenewModalClientId(clientId);
    setRenewModalOpen(true);
  }
  function showModal(token?: TokenType) {
    if (token) {
      setFormModalData({
        ...token,
        expireDate: toUtc({
          date: token.expireDate,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.ui.utcDate,
        }).object,
      });
    } else {
      setFormModalData(initialTokenValues);
    }
    setFormModalOpen(true);
  }

  const data: ListCardProps["dataSource"] = (tokenList || []).map((token) => ({
    key: token.clientId || "",
    actions: [
      <a key={0}>
        <TableAction Icon={Edit} iconColor={"cta"} onClick={() => showModal(token)} label={t("edit")} />
      </a>,
      <a key={1}>
        <TableAction
          key={1}
          Icon={SwapOutlined}
          iconColor={"primaryLite"}
          onClick={() => handleRenew(token.clientId || "")}
          label={t("token.renew.renewToken")}
        />
      </a>,
      <a key={1}>
        <TableAction
          Icon={Delete}
          iconColor={"danger"}
          label={t("delete")}
          onClick={(): void => handleDelete(token.clientId || "")}
          key={2}
        />
      </a>,
    ],

    descriptions: (
      <Flex gap={"small"}>
        <Website width={14} color={"black"} />
        <Typography.Text>
          {t("token.websiteAddress")} {token.clientUri}
        </Typography.Text>
      </Flex>
    ),
    content: !lg
      ? [
          <>
            {(() => {
              const expireMoment = moment(
                toJalali({
                  date: token.expireDate,
                  toFormat: dateFormats.ui.jalaliDateTime,
                }).string,
                dateFormats.ui.jalaliDateTime,
              );
              const nowMoment = moment(jalaliNow().string, dateFormats.ui.jalaliDateTime);
              const isExpired = expireMoment.isBefore(nowMoment);

              if (isExpired) {
                return <Tag icon={<FiClock width={14} />}>{t("coupon.status.EXPIRED")}</Tag>;
              }

              // @ts-ignore
              return token.isVerify ? (
                <Tag color={"green"} icon={<PhRCheckCircle width={14} />}>
                  {t("overview.completeProfile.status.confirmed")}
                </Tag>
              ) : (
                <Tag color={"red"} icon={<PhRxCircle width={14} />}>
                  {t("unverified")}
                </Tag>
              );
            })()}
          </>,
        ]
      : [],
    title: {
      primary: (
        <Space>
          <Typography.Title level={5} className={"!tw-m-0 tw-font-normal"}>
            {token.clientName}
          </Typography.Title>
          <Typography.Text type={"secondary"}>
            {t("expire")}
            {moment
              .duration(
                toJalali({ date: token.expireDate, toFormat: dateFormats.ui.jalaliDateTime }).object?.diff(
                  jalaliNow().object,
                ),
              )
              .locale(language)
              .humanize(true)}
          </Typography.Text>
          {lg ? (
            <>
              {(() => {
                const expireMoment = moment(
                  toJalali({
                    date: token.expireDate,
                    toFormat: dateFormats.ui.jalaliDateTime,
                  }).string,
                  dateFormats.ui.jalaliDateTime,
                );
                const nowMoment = moment(jalaliNow().string, dateFormats.ui.jalaliDateTime);
                const isExpired = expireMoment.isBefore(nowMoment);

                if (isExpired) {
                  return <Tag icon={<FiClock width={14} />}>{t("coupon.status.EXPIRED")}</Tag>;
                }

                // @ts-ignore
                return token.isVerify ? (
                  <Tag color={"green"} icon={<PhRCheckCircle width={14} />}>
                    {t("overview.completeProfile.status.confirmed")}
                  </Tag>
                ) : (
                  <Tag color={"red"} icon={<PhRxCircle width={14} />}>
                    {t("unverified")}
                  </Tag>
                );
              })()}
            </>
          ) : null}
        </Space>
      ),
    },
  }));

  // TODO: getClientTokenList method doesn't accept offset and limit for pagination
  const { loading } = useMessagedAsync(() => dispatch(getClientTokenList()), [dispatch], { useProgress: true });

  return (
    <Drawer
      open={drawerOpen}
      title={t("connections.token.tokens")}
      height={"100%"}
      width={"100%"}
      placement={"bottom"}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            setDrawerOpen(false);
            setTimeout(() => navigate(-1), 300);
          }}
        />
      }
    >
      <Space direction={"vertical"} size={"middle"} className={"tw-w-full"}>
        {loading || tokenList?.length > 0 ? (
          <CardList
            className={"tw-w-full"}
            loading={loading}
            // eslint-disable-next-line sort-keys
            grid={{ xs: 1, sm: 1, md: 1, lg: 3, xl: 4, xxl: 4 }}
            dataSource={data}
            mode={lg ? DisplayModeEnum.List : DisplayModeEnum.Card}
            bordered={false}
          />
        ) : null}
      </Space>
      <TokenFormModal
        open={formModalOpen}
        setOpen={setFormModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        formModalData={formModalData}
        setConfirmModalData={setConfirmModalData}
        modal={modal}
      />
      <TokenRenewModal
        open={renewModalOpen}
        setOpen={setRenewModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        setConfirmModalData={setConfirmModalData}
        clientId={renewModalClientId}
      />
      <ConfirmModal open={confirmModalOpen} setOpen={setConfirmModalOpen} {...confirmModalData} />
      {contextHolder}
    </Drawer>
  );
};

export default TokenList;
